/* iter465 — Skin & Joints Pack: COL3 daily contribution tracker.
 *
 * Visualises how the user's foodlab-tracked COL3 score accumulates over
 * the last 7 days, with an 8-week projected improvement % for skin/joints.
 * Premium driver tile inside Health Intelligence Dashboard.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { Sparkles, Loader2, TrendingUp } from "lucide-react";

const barTone = (v) => {
  if (v >= 60) return "from-emerald-400 to-emerald-600";
  if (v >= 40) return "from-amber-400 to-amber-600";
  if (v > 0)   return "from-rose-300 to-rose-500";
  return "from-slate-300 to-slate-400";
};

export default function Col3TrackerTile({ isDark, bg }) {
  const { API } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/foodlab/col3-tracker?days=7`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ daily: [], avg_col3: 0, projected_improvement: null }));
  }, [API]);

  const card = isDark ? "bg-[hsl(223,47%,9%)] border-[hsl(223,47%,16%)]" : "bg-white border-slate-200";
  const txt = isDark ? "text-white" : "text-slate-900";
  const sub = isDark ? "text-slate-400" : "text-slate-500";

  const avg = data?.avg_col3 ?? 0;
  const projection = data?.projected_improvement;

  return (
    <div className={`rounded-2xl border p-4 ${card}`} data-testid="col3-tracker-tile">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className={`text-sm font-bold ${txt}`}>
              {bg ? "Skin & Joints Pack — COL3 трекер" : "Skin & Joints Pack — COL3 tracker"}
            </h3>
            <p className={`text-[10px] ${sub}`}>
              {bg ? "Ежедневен принос на колаген (последни 7 дни)" : "Daily collagen contribution (last 7 days)"}
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-bold ${txt}`} data-testid="col3-avg-value">{avg}</div>
          <div className={`text-[10px] ${sub}`}>{bg ? "среден COL3" : "avg COL3"}</div>
        </div>
      </div>

      {/* Bar chart */}
      {!data && (
        <div className="flex items-center justify-center py-6">
          <Loader2 className={`w-5 h-5 animate-spin ${sub}`} />
        </div>
      )}
      {data && (
        <div className="flex items-end gap-1.5 h-20 mb-3">
          {(data.daily || []).map((d) => {
            const heightPct = Math.max(5, (d.col3_score / 100) * 100);
            const label = new Date(d.date).toLocaleDateString(bg ? "bg-BG" : "en-US", { weekday: "short" });
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1" data-testid={`col3-bar-${d.date}`}>
                <div className="w-full flex-1 flex items-end">
                  <div
                    className={`w-full rounded-t bg-gradient-to-t ${barTone(d.col3_score)}`}
                    style={{ height: `${heightPct}%` }}
                    title={`${d.col3_score}/100 (${d.entries} ${bg ? "записа" : "entries"})`}
                  />
                </div>
                <div className={`text-[9px] ${sub}`}>{label}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Projection */}
      {projection && (
        <div
          data-testid="col3-projection"
          className={`flex items-start gap-2 p-2.5 rounded-lg ${
            avg >= 60
              ? (isDark ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-emerald-50 border border-emerald-200")
              : avg >= 40
              ? (isDark ? "bg-amber-500/10 border border-amber-500/30" : "bg-amber-50 border border-amber-200")
              : (isDark ? "bg-slate-800/40 border border-slate-700" : "bg-slate-50 border border-slate-200")
          }`}
        >
          <TrendingUp className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${avg >= 60 ? "text-emerald-500" : avg >= 40 ? "text-amber-500" : sub}`} />
          <div className="flex-1">
            <div className={`text-xs font-bold ${txt}`}>
              {projection.improvement_pct}% {bg ? "потенциално подобрение" : "potential improvement"} · {projection.weeks} {bg ? "седмици" : "weeks"}
            </div>
            <p className={`text-[11px] leading-snug mt-0.5 ${sub}`}>
              {bg ? projection.msg_bg : projection.msg_en}
            </p>
          </div>
        </div>
      )}

      <p className={`text-[10px] text-center mt-2 ${sub}`}>
        {bg ? "Educational · Не е медицинска препоръка." : "Educational · Not medical advice."}
      </p>
    </div>
  );
}
